import { useRef } from 'react';

const PRINT_LOCK_STALE_MS = 20000;
const PRINT_FALLBACK_CLEANUP_MS = 8000;
const PRINT_INIT_DELAY_MS = 300;
const ANDROID_PRINT_INIT_DELAY_MS = 700;
const PRINT_FRAME_ATTR = 'data-receipt-print-frame';
const PRINT_BODY_CLASS = 'receipt-printing';
const PRINT_TARGET_ATTR = 'data-receipt-print-target';
const MAX_RECEIPT_COPY_COUNT = 10;
const PX_TO_MM = 25.4 / 96;

const isAndroid = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return /Android/i.test(navigator.userAgent);
};

const getPrintLock = () => {
    if (typeof window === 'undefined') {
        return null;
    }
    if (!window.__TF_RECEIPT_PRINT_LOCK__) {
        window.__TF_RECEIPT_PRINT_LOCK__ = { token: null, ts: 0 };
    }
    return window.__TF_RECEIPT_PRINT_LOCK__;
};

const acquirePrintLock = (token) => {
    const lock = getPrintLock();
    if (!lock) {
        return true;
    }
    const now = Date.now();
    if (lock.token && now - lock.ts < PRINT_LOCK_STALE_MS) {
        console.warn('[ReceiptPrint] blocked: print already in progress', lock);
        return false;
    }
    lock.token = token;
    lock.ts = now;
    return true;
};

const releasePrintLock = (token) => {
    const lock = getPrintLock();
    if (!lock) {
        return;
    }
    if (!token || lock.token === token) {
        lock.token = null;
        lock.ts = 0;
    }
};

const isDebugEnabled = () => {
    if (typeof window === 'undefined') {
        return false;
    }
    try {
        return window.__TF_RECEIPT_DEBUG__ === true || window.localStorage?.getItem('receipt_print_debug') === '1';
    } catch (error) {
        return false;
    }
};

const debugLog = (...args) => {
    if (isDebugEnabled()) {
        console.log('[ReceiptPrint]', ...args);
    }
};

const removeExistingPrintArtifacts = () => {
    if (typeof document === 'undefined') {
        return;
    }
    const frames = document.querySelectorAll(`iframe[${PRINT_FRAME_ATTR}="1"]`);
    frames.forEach((frame) => frame.remove());
    const oldRoots = document.querySelectorAll('.receipt-print-root');
    oldRoots.forEach((root) => root.remove());
    const oldStyles = document.querySelectorAll('style[data-receipt-print="1"]');
    oldStyles.forEach((style) => style.remove());
    document.body?.classList.remove(PRINT_BODY_CLASS);
    const markedTargets = document.querySelectorAll(`.pos-receipt-modal[${PRINT_TARGET_ATTR}]`);
    markedTargets.forEach((target) => target.removeAttribute(PRINT_TARGET_ATTR));
};

const collectStyleHtml = () => {
    if (typeof document === 'undefined') {
        return '';
    }
    const nodes = document.querySelectorAll('style, link[rel="stylesheet"]');
    return Array.from(nodes).map((node) => node.outerHTML).join('');
};

const normalizeCopyCount = (value) => {
    const parsed = Number.parseInt(String(value ?? ''), 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return 1;
    }
    return Math.min(parsed, MAX_RECEIPT_COPY_COUNT);
};

const toReceiptHeightMm = (contentHeightPx) => {
    const heightMm = Number(contentHeightPx || 0) * PX_TO_MM;
    return Math.max(25, Math.ceil(heightMm + 2));
};

const buildReceiptHtml = (target, copyCount = 1, receiptWidthMm = 80) => {
    const safeCopyCount = normalizeCopyCount(copyCount);
    const copyHtml = Array.from({ length: safeCopyCount }, (_, index) => {
        return `
            <section class="receipt-copy" data-receipt-copy-index="${index + 1}">
                <div
                    class="receipt-sheet"
                    data-receipt-width-mm="${receiptWidthMm}"
                    style="width:${receiptWidthMm}mm;max-width:${receiptWidthMm}mm;"
                >
                    ${target.innerHTML}
                </div>
            </section>
        `;
    }).join('');

    return `
        <div class="pos-receipt-modal">
            <div class="receipt-print-shell">
                ${copyHtml}
            </div>
        </div>
    `;
};

const markActiveReceipt = (target) => {
    if (typeof document === 'undefined') {
        return null;
    }
    const modal = target?.closest?.('.pos-receipt-modal');
    if (modal) {
        modal.setAttribute(PRINT_TARGET_ATTR, '1');
    }
    document.body?.classList.add(PRINT_BODY_CLASS);
    return modal || null;
};

const clearActiveReceipt = (modal) => {
    if (typeof document === 'undefined') {
        return;
    }
    if (modal) {
        modal.removeAttribute(PRINT_TARGET_ATTR);
    }
    document.body?.classList.remove(PRINT_BODY_CLASS);
};

const PritntPdfReceipt = (copyCount = 1) => {
    const printRef = useRef(null);
    const isPrintingRef = useRef(false);

    const createJobId = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
    };

    const handleDownloadPdf = () => {
        if (!printRef?.current) {
            console.error('Print reference is not defined');
            return;
        }

        if (isPrintingRef.current) {
            console.warn('Print already in progress, ignoring duplicate call');
            return;
        }

        const jobId = createJobId();
        if (!acquirePrintLock(jobId)) {
            console.warn('Print blocked by global lock');
            return;
        }

        isPrintingRef.current = true;
        debugLog('start', { jobId, isAndroid: isAndroid() });

        removeExistingPrintArtifacts();

        const target = printRef.current;
        const activeModal = markActiveReceipt(target);
        const widthFromData = Number.parseFloat(target?.dataset?.receiptWidthMm || '');
        const receiptWidthMm =
            Number.isFinite(widthFromData) && widthFromData >= 40 && widthFromData <= 120
                ? widthFromData
                : 80;
        const printFrame = document.createElement('iframe');
        printFrame.setAttribute(PRINT_FRAME_ATTR, '1');
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';
        printFrame.style.visibility = 'hidden';
        printFrame.style.pointerEvents = 'none';
        document.body.appendChild(printFrame);

        const frameDoc = printFrame.contentWindow?.document;
        if (!frameDoc) {
            console.error('Cannot access print frame document');
            isPrintingRef.current = false;
            releasePrintLock(jobId);
            return;
        }

        let cleanedUp = false;
        const cleanup = (reason) => {
            if (cleanedUp) {
                return;
            }
            cleanedUp = true;
            debugLog('cleanup', { jobId, reason });
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
            clearActiveReceipt(activeModal);
            isPrintingRef.current = false;
            releasePrintLock(jobId);
        };

        const stylesHtml = collectStyleHtml();
        const receiptHtml = buildReceiptHtml(target, copyCount, receiptWidthMm);

        frameDoc.open();
        frameDoc.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Receipt</title>
                    ${stylesHtml}
                    <style id="receipt-page-size-style">
                        @page {
                            size: ${receiptWidthMm}mm auto;
                            margin: 0;
                        }
                    </style>
                    <style>
                        html, body {
                            margin: 0;
                            padding: 0;
                            width: ${receiptWidthMm}mm;
                            min-width: ${receiptWidthMm}mm;
                            overflow: visible !important;
                        }
                        body {
                            background: #ffffff;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                            display: block;
                        }
                        .pos-receipt-modal,
                        .pos-receipt-modal .receipt-print-shell,
                        .pos-receipt-modal .receipt-copy {
                            position: static !important;
                            left: auto !important;
                            top: auto !important;
                            transform: none !important;
                            width: ${receiptWidthMm}mm !important;
                            height: auto !important;
                            overflow: visible !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            background: #ffffff !important;
                        }
                        .pos-receipt-modal .receipt-print-shell {
                            margin: 0 !important;
                            width: ${receiptWidthMm}mm !important;
                            max-width: ${receiptWidthMm}mm !important;
                            box-sizing: border-box !important;
                            min-height: 0 !important;
                            height: auto !important;
                            padding: 0 !important;
                            overflow: visible !important;
                        }
                        .pos-receipt-modal .receipt-copy {
                            width: 100% !important;
                            page-break-after: always;
                            break-after: page;
                            display: block !important;
                        }
                        .pos-receipt-modal .receipt-copy:last-child {
                            page-break-after: auto;
                            break-after: auto;
                        }
                        .pos-receipt-modal .receipt-sheet {
                            width: 100% !important;
                            max-width: 100% !important;
                            box-sizing: border-box !important;
                            min-height: 0 !important;
                            height: auto !important;
                            padding: 0.75mm 1.5mm !important;
                            margin: 0 !important;
                            overflow: visible !important;
                            position: static !important;
                            display: block !important;
                        }
                        .pos-receipt-modal .receipt-sheet h1,
                        .pos-receipt-modal .receipt-sheet h2,
                        .pos-receipt-modal .receipt-sheet h3,
                        .pos-receipt-modal .receipt-sheet h4,
                        .pos-receipt-modal .receipt-sheet h5,
                        .pos-receipt-modal .receipt-sheet h6,
                        .pos-receipt-modal .receipt-sheet p,
                        .pos-receipt-modal .receipt-sheet table,
                        .pos-receipt-modal .receipt-sheet tr,
                        .pos-receipt-modal .receipt-sheet td,
                        .pos-receipt-modal .receipt-sheet th {
                            margin: 0 !important;
                        }
                        .pos-receipt-modal .receipt-sheet table {
                            border-collapse: collapse !important;
                            border-spacing: 0 !important;
                        }
                        .pos-receipt-modal .receipt-sheet table th,
                        .pos-receipt-modal .receipt-sheet table td {
                            padding-top: 0.2mm !important;
                            padding-bottom: 0.2mm !important;
                        }
                        .pos-receipt-modal .receipt-sheet > :last-child {
                            margin-bottom: 0 !important;
                            padding-bottom: 0 !important;
                        }
                    </style>
                </head>
                <body>
                    ${receiptHtml}
                </body>
            </html>
        `);
        frameDoc.close();

        const printDelay = isAndroid() ? ANDROID_PRINT_INIT_DELAY_MS : PRINT_INIT_DELAY_MS;
        let didPrint = false;

        const updatePrintPageHeight = () => {
            try {
                const frameBody = frameDoc.body;
                const receiptBody = frameDoc.querySelector('.pos-receipt-modal .receipt-sheet');
                const receiptRoot = frameDoc.querySelector('.pos-receipt-modal .receipt-print-shell');
                const sizingStyle = frameDoc.getElementById('receipt-page-size-style');
                if (!frameBody || !receiptBody || !receiptRoot || !sizingStyle) {
                    return null;
                }

                const measuredPx = Math.max(
                    receiptRoot.scrollHeight || 0,
                    receiptRoot.offsetHeight || 0,
                    receiptBody.scrollHeight || 0,
                    receiptBody.offsetHeight || 0
                );
                const pageHeightMm = toReceiptHeightMm(measuredPx);

                sizingStyle.textContent = `
                    @page {
                        size: ${receiptWidthMm}mm ${pageHeightMm}mm;
                        margin: 0;
                    }
                `;

                debugLog('measured page height', { jobId, measuredPx, pageHeightMm });
                return pageHeightMm;
            } catch (error) {
                debugLog('page height measurement failed', { jobId, error });
                return null;
            }
        };

        const schedulePrint = (reason) => {
            if (didPrint) {
                debugLog('skip print', { jobId, reason });
                return;
            }
            didPrint = true;
            debugLog('schedule print', { jobId, reason });
            setTimeout(() => {
                updatePrintPageHeight();
                try {
                    printFrame.contentWindow?.focus();
                    printFrame.contentWindow?.print();
                    debugLog('print called', { jobId });
                } catch (error) {
                    console.error('Print error:', error);
                    cleanup('print-error');
                    return;
                }
                setTimeout(() => cleanup('fallback-timeout'), PRINT_FALLBACK_CLEANUP_MS);
            }, printDelay);
        };

        printFrame.addEventListener('load', () => schedulePrint('load'), { once: true });
        try {
            printFrame.contentWindow?.addEventListener('afterprint', () => cleanup('afterprint'), { once: true });
        } catch (error) {
            debugLog('afterprint attach failed', { jobId, error });
        }

        if (frameDoc.readyState === 'complete' || frameDoc.readyState === 'interactive') {
            schedulePrint('readyState');
        }
    };

    return { printRef, handleDownloadPdf };
};

export default PritntPdfReceipt;
